/**
 * Parses the Velcoda backend error shape:
 * `{ code: <http status>, message: 'HTTP_BAD_REQUEST' | …, details?: string, reasons?: Record<string, string[]> }`.
 * `message` is a machine code, never display text. Human-readable text comes from `details`
 * (several messages joined by `'; '`) or from a `reasons`/`errors` field map (Laravel validation).
 */
export interface ApiErrors {
  /** The machine code from `message`, e.g. `HTTP_UNPROCESSABLE_ENTITY`; null when absent. */
  code: string | null
  /** Human-readable messages; empty when the backend sent none. */
  messages: string[]
}

function flatten(raw: unknown): string[] {
  if (raw === null || raw === undefined || raw === '') return []
  if (Array.isArray(raw)) return raw.flatMap((e) => flatten(e))
  if (typeof raw === 'string') return [raw]
  if (typeof raw === 'object') return Object.values(raw as Record<string, unknown>).flatMap((e) => flatten(e))
  return [String(raw)]
}

function splitDetails(details: unknown): string[] {
  if (typeof details === 'string') {
    return details
      .split('; ')
      .map((m) => m.trim())
      .filter((m) => m !== '')
  }
  return flatten(details)
}

export function extractApiErrors(err: unknown): ApiErrors {
  const data = (err as { response?: { data?: unknown } } | null | undefined)?.response?.data
  if (!data || typeof data !== 'object') return { code: null, messages: [] }
  const body = data as Record<string, unknown>
  const code = typeof body.message === 'string' && body.message !== '' ? body.message : null
  let messages = splitDetails(body.details)
  if (messages.length === 0) messages = flatten(body.reasons)
  if (messages.length === 0) messages = flatten(body.errors)
  return { code, messages }
}
